import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription, first, forkJoin, of, switchMap, takeUntil, tap } from 'rxjs';
import { BillingPeriodData, CardsType, CustomerCardData, DashboardCardsData, PortletColumn, PortletTargetActions, Section } from 'src/app/modules/home/dashboard-widgets/models/dashboard.model';
import { DashboardService } from 'src/app/modules/home/dashboard-widgets/services/dashboard.service';
import { CommonService } from 'src/app/services/common.service';
import { LoaderService } from 'src/app/services/loader.service';
import { IsMandateFileResponse } from 'src/app/shared/models/menus.model';
import { BannerNotificationAddComponent } from '../../partner/banner-notification/components/banner-notification-add/banner-notification-add.component';
import { BannerNotificationService } from '../../partner/banner-notification/Service/banner-notification.service';
import { BannerService } from 'src/app/services/banner.service';
import { PageMode } from 'src/app/shared/models/enums/enums';
import { TranslateService } from '@ngx-translate/core';
import { EngageService } from '../../partner/engage/service/engage.service';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  entityName: string | null;
  isMandateProfile: boolean;
  portletsActions = PortletTargetActions;
  dashboardCards: Partial<DashboardCardsData>;

  notifier$ = new Subject();
  private _router: any;

  constructor(
    private dashboardService: DashboardService,
    private cdref: ChangeDetectorRef,
    private commonService: CommonService,
    private translateService: TranslateService,
    private _bannerService: BannerService,
    private notifier: NotifierService,
    private appSettingService: AppSettingsService,
    private _bannerNotification: BannerNotificationService,
    private _engageService: EngageService,
    private userContext: UserContextService,

  ) { }

  /**
   * Component Ng OnInit
   */
  ngOnInit(): void {
    this.entityName = this.commonService.entityName;

    this.appSettingService.getUserContext().pipe(first()).subscribe((res: any) => {
      if (res.Data == undefined || res.Data == null || res.Data.length <= 0) {
        this.userNotFound();
      }
    })
    this.appSettingService.getAvailableEnvironments().pipe(first()).subscribe((res: any) => {
      if (res.Data == undefined || res.Data == null || res.Data.length <= 0) {
        this.userNotFound();
      }
    })

    // get the dashboard data
    this.handleDashboardCardData();
    this.renderbanner();
    this.loadEngageNotification();
  }

  userNotFound() {
    let title = this.translateService.instant('TRANSLATE.USER_DETAILS_NOT_FOUND_HEADER');
    let body = this.translateService.instant('TRANSLATE.USER_DETAILS_NOT_FOUND_BODY');
    this.notifier.alert({ title: title, text: body }).then((result: { isConfirmed: any }) => {
      this.userContext.logOut();
    });
  }

  /**
   * Retrieve the dashboard data
   */
  handleDashboardCardData(): void {
    this.dashboardService.GetIsMandateProfile()
      .pipe(
        takeUntil(this.notifier$),

        switchMap((data: Partial<IsMandateFileResponse>) => {
          this.isMandateProfile = data?.Data?.IsMandateProfile != null ? data.Data.IsMandateProfile : true;
          return !this.isMandateProfile ? this.dashboardService.getDashboardCards() : of(null);
        })
      )
      .subscribe((res: any) => {
        this.dashboardCards = res?.Data;
        if (this.dashboardCards?.Sections) {
          this.dashboardCards.Sections.forEach((section: Section) => {
            section.PortletColumns = section.PortletColumns.map((columnData: PortletColumn) => ({
              ID: columnData?.ID || 0,
              ColumnSizeClass: columnData?.ColumnSizeClass || '',
              PortletSectionID: columnData?.PortletSectionID || 0,
              DisplayOrder: columnData?.DisplayOrder || 0,
              Show: true
            }));
          });
          this.cdref.detectChanges();
        }
      });
  }

  /**
   *
   * @param section
   * @param column
   * @returns
   */
  getPortletsBySectionAndColumn(section: Section, column: PortletColumn): any[] {
    const portlets = this.dashboardCards?.Portlets?.filter((item: any) => {
      return (item.PortletSectionID === section?.ID &&
        item.ColumnPosition === (column.DisplayOrder - 1));
    });

    //KB: If the Portlets count is zero, mark the column as hidden.
    column.Show = (portlets != null && portlets.length > 0);
    return portlets || [];
  }

  renderbanner() {
    this._bannerNotification.loadBanner('Dashboard').subscribe((response: any) => {
      if (response.Status === 'Success' && response.Data !== null && response.Data?.[0]?.MessageBody) {
        const messageBody = this.translateService.instant(response.Data[0]?.MessageBody);
        const messageType = response.Data[0]?.MessageType;
        this._bannerService.show(messageType, messageBody)
      }
    })
  }

  loadEngageNotification() {
    let pageName: string;
    if (this.entityName === 'Partner' || this.entityName === 'Reseller') {
      pageName = "PartnerDashboard";
    } else if (this.entityName === 'Customer') {
      pageName = "CustomerDashboard";
    }
    this._engageService.engageNotification(pageName).subscribe((response: any) => {
      if (response.Status === 'Success' && response.Data !== null && response.Data.length > 0) {
        let loadEngageNotification = response.Data[0];

        const data = {
          title: loadEngageNotification.Title,
          description: loadEngageNotification.BodyText,
          btnColor: loadEngageNotification.BtnColor,
          btnText: loadEngageNotification.BtnText,
          btnTextColor: loadEngageNotification.BtnTextColor,
          btnUrl: loadEngageNotification.ButtonUrl
        }

        this._bannerService.showWithTemplate(loadEngageNotification.BackgroundTemplate, data);
      }
    })
  }

  /**
   * Component Destroyed
   */
  ngOnDestroy(): void {
    this.notifier$?.next(null);
    this.notifier$?.complete;
    this._bannerService.clear();
  }
}
